import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";

const FONT_LINK = `
  @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,400;0,500;0,600;0,700;1,400;1,600&family=Montserrat:wght@300;400;500;600;700;800&display=swap');
`;

const GLOBAL_CSS = `
${FONT_LINK}

*, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

:root {
  --black: #080808;
  --dark: #0E0E0E;
  --card: #141414;
  --border: rgba(255,255,255,0.07);
  --gold: #D4AF37;
  --gold2: #F0D060;
  --gold3: #C9A227;
  --gold-dim: rgba(212,175,55,0.18);
  --white: #FFFFFF;
  --muted: rgba(255,255,255,0.45);
  --muted2: rgba(255,255,255,0.25);
  --serif: 'Cormorant Garamond', serif;
  --sans: 'Montserrat', sans-serif;
  --bg: #080808;
  --surface: #141414;
  --surface2: #1a1a1a;
  --text-primary: #FFFFFF;
  --text-muted: rgba(255,255,255,0.45);
  --text-muted2: rgba(255,255,255,0.25);
  --border-color: rgba(255,255,255,0.07);
  --border-gold: rgba(212,175,55,0.2);
  --gold-subtle: rgba(212,175,55,0.08);
  --gold-subtle2: rgba(212,175,55,0.12);
}

/* ── LIGHT THEME ── */
.lp-light {
  --bg: #FFFFFF;
  --surface: #F5F3EE;
  --surface2: #EDE9E0;
  --card: #F5F3EE;
  --dark: #EDE9E0;
  --black: #FFFFFF;
  --border-color: rgba(0,0,0,0.08);
  --border: rgba(0,0,0,0.08);
  --border-gold: rgba(180,140,30,0.25);
  --white: #0E0E0E;
  --text-primary: #111111;
  --text-muted: rgba(0,0,0,0.5);
  --text-muted2: rgba(0,0,0,0.35);
  --muted: rgba(0,0,0,0.5);
  --muted2: rgba(0,0,0,0.35);
  --gold: #A0780A;
  --gold2: #C9991A;
  --gold3: #8A6608;
  --gold-dim: rgba(160,120,10,0.15);
  --gold-subtle: rgba(160,120,10,0.08);
  --gold-subtle2: rgba(160,120,10,0.12);
}

html { scroll-behavior: smooth; }
body {
  background: var(--bg);
  color: var(--text-primary);
  font-family: var(--sans);
  overflow-x: hidden;
  letter-spacing: 0.1px;
}

::-webkit-scrollbar { width: 4px; }
::-webkit-scrollbar-track { background: var(--bg); }
::-webkit-scrollbar-thumb { background: rgba(212,175,55,0.3); border-radius: 2px; }

/* ═══════════════════════════════════════
   ENHANCEMENT 3 — DIRECTIONAL STAGGER REVEALS
   Elements animate from different directions
═══════════════════════════════════════ */
.reveal {
  opacity: 0;
  transition: opacity 0.75s cubic-bezier(0.22,1,0.36,1), transform 0.75s cubic-bezier(0.22,1,0.36,1);
}
.reveal.from-up    { transform: translateY(32px); }
.reveal.from-down  { transform: translateY(-24px); }
.reveal.from-left  { transform: translateX(-40px); }
.reveal.from-right { transform: translateX(40px); }
.reveal.from-scale { transform: scale(0.92); }

/* stagger delays */
.reveal.d1 { transition-delay: 0.05s; }
.reveal.d2 { transition-delay: 0.13s; }
.reveal.d3 { transition-delay: 0.21s; }
.reveal.d4 { transition-delay: 0.29s; }
.reveal.d5 { transition-delay: 0.37s; }
.reveal.d6 { transition-delay: 0.45s; }

.reveal.visible {
  opacity: 1;
  transform: translateY(0) translateX(0) scale(1);
}

/* ── NAV ── */
.lp-nav {
  position: fixed; top: 0; left: 0; right: 0; z-index: 200;
  padding: 1.5rem 3rem; transition: all 0.4s;
}
.lp-nav.scrolled {
  background: rgba(8,8,8,0.9);
  backdrop-filter: blur(20px);
  border-bottom: 0.5px solid rgba(212,175,55,0.12);
  padding: 1rem 3rem;
}
.lp-light .lp-nav.scrolled {
  background: rgba(255,255,255,0.92);
  border-bottom: 0.5px solid rgba(160,120,10,0.18);
}
.lp-nav-inner { max-width: 1180px; margin: 0 auto; display: flex; align-items: center; justify-content: space-between; }
.lp-logo {
  display: flex; align-items: center; gap: 10px;
  font-family: var(--sans); font-size: 15px; font-weight: 600;
  color: var(--text-primary); text-decoration: none; letter-spacing: 0.2px;
}
.lp-logo-icon { width: 34px; height: 34px; flex-shrink: 0; }
.lp-logo em { color: var(--gold); font-style: normal; font-weight: 700; }
.lp-nav-links { display: flex; align-items: center; gap: 2.5rem; }
.lp-nav-link { font-size: 13px; font-weight: 500; color: var(--text-muted); text-decoration: none; letter-spacing: 0.3px; transition: color 0.2s; }
.lp-nav-link:hover { color: var(--text-primary); }
.lp-nav-cta {
  padding: 10px 22px;
  background: linear-gradient(135deg, #D4AF37, #F0D060, #C9A227);
  color: #0A0A0A; border: none; border-radius: 10px;
  font-family: var(--sans); font-size: 13px; font-weight: 700;
  letter-spacing: 0.8px; text-transform: uppercase;
  cursor: pointer; position: relative; overflow: hidden;
  transition: transform 0.15s, opacity 0.15s;
}
.lp-nav-cta::before { content: ''; position: absolute; inset: 0; background: linear-gradient(135deg, rgba(255,255,255,0.2), transparent 60%); pointer-events: none; }
.lp-nav-cta:hover { transform: translateY(-1px); opacity: 0.92; }

/* ── HERO ── */
.lp-hero {
  min-height: 100vh;
  display: grid; grid-template-columns: 1fr 1fr;
  align-items: center; gap: 4rem;
  max-width: 1180px; margin: 0 auto;
  padding: 10rem 3rem 3rem; position: relative;
}
.lp-hero-left { position: relative; z-index: 2; }
.lp-hero-eyebrow {
  display: inline-flex; align-items: center; gap: 8px;
  padding: 6px 14px;
  background: var(--gold-subtle2); border: 0.5px solid var(--border-gold);
  border-radius: 100px; font-size: 11px; font-weight: 700; color: var(--gold);
  letter-spacing: 1.5px; text-transform: uppercase; margin-bottom: 2rem;
}
.lp-hero-eyebrow-dot { width: 5px; height: 5px; border-radius: 50%; background: var(--gold); animation: pulse-gold 2s ease-in-out infinite; }
@keyframes pulse-gold { 0%,100%{opacity:1;transform:scale(1);} 50%{opacity:0.4;transform:scale(0.7);} }

.lp-hero-title { font-family: var(--serif); font-size: clamp(3.2rem, 5.8vw, 5.3rem); font-weight: 600; line-height: 1.02; letter-spacing: -0.8px; color: var(--text-primary); margin-bottom: 1.75rem; }
.lp-hero-title em { font-style: italic; font-weight: 600; color: var(--gold); }
.lp-hero-sub { font-size: 15px; font-weight: 400; color: var(--text-muted); line-height: 1.9; max-width: 470px; margin-bottom: 1rem; }
.lp-hero-ctas { display: flex; gap: 1rem; flex-wrap: wrap; }

.lp-btn-gold {
  padding: 16px 32px;
  background: linear-gradient(135deg, #D4AF37, #F0D060, #C9A227);
  color: #0A0A0A; border: none; border-radius: 12px;
  font-family: var(--sans); font-size: 13px; font-weight: 700;
  letter-spacing: 1px; text-transform: uppercase;
  cursor: pointer; position: relative; overflow: hidden;
  transition: transform 0.2s, box-shadow 0.2s;
  display: flex; align-items: center; gap: 8px;
}
.lp-btn-gold::before { content:''; position:absolute; inset:0; background:linear-gradient(135deg,rgba(255,255,255,0.25),transparent 60%); pointer-events:none; }
.lp-btn-gold:hover { transform: translateY(-2px); box-shadow: 0 8px 28px rgba(212,175,55,0.3); }

.lp-btn-ghost {
  padding: 16px 28px; background: transparent;
  color: var(--text-muted); border: 0.5px solid var(--border-color);
  border-radius: 12px; font-family: var(--sans); font-size: 13px; font-weight: 600;
  letter-spacing: 0.5px; cursor: pointer; text-decoration: none;
  display: inline-flex; align-items: center; gap: 8px;
  transition: border-color 0.2s, color 0.2s;
}
.lp-btn-ghost:hover { border-color: var(--border-gold); color: var(--text-primary); }

/* ═══════════════════════════════════════
   ENHANCEMENT 1 — COUNT-UP STATS
═══════════════════════════════════════ */
.lp-hero-stats { display: flex; gap: 2.5rem; padding-bottom: 1.5rem; margin-bottom: 1.5rem; }
.lp-stat { position: relative; }
.lp-stat-num {
  font-family: var(--serif); font-size: 2.4rem; font-weight: 600;
  color: var(--gold); line-height: 1;
  /* subtle gold shimmer when counting */
}
.lp-stat-num.counting {
  animation: stat-shimmer 0.1s ease infinite alternate;
}
@keyframes stat-shimmer {
  from { opacity: 0.7; }
  to   { opacity: 1; }
}
.lp-stat-label { font-size: 11px; font-weight: 500; color: var(--text-muted2); letter-spacing: 0.5px; margin-top: 5px; }
/* thin gold underline that fills as number counts */
.lp-stat-bar {
  height: 1.5px; background: var(--border-gold); margin-top: 8px;
  border-radius: 1px; overflow: hidden;
}
.lp-stat-bar-fill {
  height: 100%;
  background: linear-gradient(90deg, var(--gold), var(--gold2));
  border-radius: 1px;
  width: 0%;
  transition: width 0.05s linear;
}

/* ── HERO RIGHT ── */
.lp-hero-right { position: relative; display: flex; align-items: center; justify-content: center; z-index: 2; }
.scan-scene { position: relative; width: 380px; height: 480px; }

/* ═══════════════════════════════════════
   ENHANCEMENT 5 — HERO RIPPLE / SUCCESS
═══════════════════════════════════════ */
/* Ripple rings that emit from phone B when scan "completes" */
.scan-ripple {
  position: absolute;
  right: -10px; top: 50px;
  width: 60px; height: 60px;
  border-radius: 50%;
  border: 1.5px solid rgba(212,175,55,0);
  z-index: 10;
  pointer-events: none;
}
.scan-ripple.r1 { animation: ripple-out 2.5s ease-out infinite; animation-delay: 3.2s; }
.scan-ripple.r2 { animation: ripple-out 2.5s ease-out infinite; animation-delay: 3.6s; }
.scan-ripple.r3 { animation: ripple-out 2.5s ease-out infinite; animation-delay: 4.0s; }
@keyframes ripple-out {
  0%   { transform: scale(1); border-color: rgba(212,175,55,0.6); opacity: 1; }
  100% { transform: scale(3.5); border-color: rgba(212,175,55,0); opacity: 0; }
}

/* Success ping badge */
.scan-success {
  position: absolute;
  right: 8px; top: 38px;
  background: linear-gradient(135deg, #D4AF37, #F0D060);
  color: #0A0A0A;
  border-radius: 100px;
  padding: 4px 10px 4px 8px;
  font-family: var(--sans); font-size: 9px; font-weight: 700;
  letter-spacing: 0.5px;
  display: flex; align-items: center; gap: 5px;
  z-index: 12; white-space: nowrap;
  opacity: 0;
  animation: success-pop 0.5s cubic-bezier(0.34,1.56,0.64,1) forwards;
  animation-delay: 3.1s;
  box-shadow: 0 4px 16px rgba(212,175,55,0.4);
}
.scan-success-dot { width: 6px; height: 6px; border-radius: 50%; background: #0A0A0A; }
@keyframes success-pop {
  0%   { opacity: 0; transform: scale(0.5) translateY(6px); }
  100% { opacity: 1; transform: scale(1) translateY(0); }
}

/* Phone A */
.phone-scanner {
  position: absolute; left: 0; top: 60px;
  width: 155px; height: 270px; background: #1A1A1A;
  border-radius: 22px; border: 1px solid rgba(255,255,255,0.1);
  overflow: hidden; box-shadow: 0 24px 60px rgba(0,0,0,0.6);
  z-index: 3;
  animation: phone-a-intro 0.8s cubic-bezier(0.34,1.56,0.64,1) both; animation-delay: 0.3s;
}
@keyframes phone-a-intro { from{opacity:0;transform:translateX(-30px) rotate(-6deg);} to{opacity:1;transform:translateX(0) rotate(-6deg);} }
.phone-scanner-screen { width:100%; height:100%; background:#111; display:flex; flex-direction:column; align-items:center; justify-content:center; gap:12px; padding:12px; }
.scanner-viewfinder { width:100px; height:100px; border-radius:10px; position:relative; background:rgba(212,175,55,0.04); }
.scanner-viewfinder::before,.scanner-viewfinder::after { content:''; position:absolute; width:20px; height:20px; border-color:#D4AF37; border-style:solid; }
.scanner-viewfinder::before { top:0;left:0;border-width:2px 0 0 2px;border-radius:6px 0 0 0; }
.scanner-viewfinder::after  { bottom:0;right:0;border-width:0 2px 2px 0;border-radius:0 0 6px 0; }
.vf-corner-tr { position:absolute;top:0;right:0;width:20px;height:20px;border-top:2px solid #D4AF37;border-right:2px solid #D4AF37;border-radius:0 6px 0 0; }
.vf-corner-bl { position:absolute;bottom:0;left:0;width:20px;height:20px;border-bottom:2px solid #D4AF37;border-left:2px solid #D4AF37;border-radius:0 0 0 6px; }
.scan-laser { position:absolute;left:4px;right:4px;height:2px;background:linear-gradient(90deg,transparent,#D4AF37,transparent);border-radius:1px;top:4px;animation:laser-sweep 2s ease-in-out infinite;animation-delay:1.2s; }
@keyframes laser-sweep { 0%{top:8px;opacity:0;} 10%{opacity:1;} 90%{opacity:1;} 100%{top:88px;opacity:0;} }

/* scanner screen flashes green briefly on scan success */
.scanner-success-flash {
  position: absolute; inset: 0;
  background: rgba(212,175,55,0.12);
  opacity: 0;
  animation: flash-once 0.4s ease forwards;
  animation-delay: 3.0s;
  pointer-events: none; z-index: 10;
}
@keyframes flash-once { 0%{opacity:0;} 40%{opacity:1;} 100%{opacity:0;} }

.scanner-label { font-size:9px;font-weight:600;color:rgba(255,255,255,0.4);letter-spacing:1px;text-align:center;text-transform:uppercase; }
.mini-qr { position:absolute;inset:10px;display:grid;grid-template-columns:repeat(6,1fr);grid-template-rows:repeat(6,1fr);gap:1.5px;opacity:0.6; }
.mini-qr-cell { border-radius:1px; }

/* Phone B */
.phone-profile {
  position:absolute;right:0;top:0;width:175px;height:310px;background:#0E0E0E;
  border-radius:24px;border:1px solid rgba(212,175,55,0.2);overflow:hidden;
  box-shadow:0 32px 80px rgba(0,0,0,0.7),0 0 0 1px rgba(212,175,55,0.08);
  z-index:4;
  animation:phone-b-intro 0.8s cubic-bezier(0.34,1.56,0.64,1) both;animation-delay:0.5s;
}
@keyframes phone-b-intro { from{opacity:0;transform:translateY(30px) rotate(4deg);} to{opacity:1;transform:translateY(0) rotate(4deg);} }
.profile-card-wrap { width:100%;height:100%;animation:card-reveal 0.7s cubic-bezier(0.22,1,0.36,1) both;animation-delay:2s; }
@keyframes card-reveal { from{transform:translateY(40px);opacity:0;} to{transform:translateY(0);opacity:1;} }
.profile-banner { width:100%;height:110px;background:linear-gradient(160deg,#1a1a1a,#2a2020);position:relative;overflow:hidden; }
.profile-banner-img { width:100%;height:100%;background:linear-gradient(160deg,#1e1a10 0%,#2a2010 50%,#181818 100%); }
.profile-banner-fade { position:absolute;bottom:0;left:0;right:0;height:50px;background:linear-gradient(transparent,#0E0E0E); }
.profile-avatar-ring { position:absolute;bottom:2px;left:14px;width:44px;height:44px;border-radius:50%;border:2px solid #0E0E0E;background:#222;overflow:hidden;box-shadow:0 0 0 1.5px rgba(212,175,55,0.5);animation:avatar-pop 0.4s cubic-bezier(0.34,1.56,0.64,1) both;animation-delay:2.4s; }
@keyframes avatar-pop { from{transform:scale(0.3);opacity:0;} to{transform:scale(1);opacity:1;} }
.profile-avatar-inner { width:100%;height:100%;background:linear-gradient(145deg,#2a2010,#3a3020);display:flex;align-items:center;justify-content:center;font-family:'Cormorant Garamond',serif;font-size:14px;font-weight:600;color:rgba(212,175,55,0.8);letter-spacing:1px; }
.profile-info { padding:28px 14px 12px; }
.profile-info-name { font-family:'Cormorant Garamond',serif;font-size:16px;font-weight:600;color:#FFF;line-height:1.2;margin-bottom:2px; }
.profile-info-name em { font-style:italic;color:#D4AF37; }
.profile-info-role { font-size:8px;font-weight:600;color:rgba(255,255,255,0.45);letter-spacing:0.3px;margin-bottom:10px; }
.profile-tiles { display:grid;grid-template-columns:1fr 1fr 1fr;gap:5px;margin-bottom:8px; }
.profile-tile { background:#1A1A1A;border:0.5px solid rgba(255,255,255,0.06);border-radius:8px;padding:7px 6px;display:flex;flex-direction:column;gap:4px;animation:tile-slide 0.3s ease both; }
.profile-tile:nth-child(1){animation-delay:2.5s;} .profile-tile:nth-child(2){animation-delay:2.65s;} .profile-tile:nth-child(3){animation-delay:2.8s;}
@keyframes tile-slide { from{opacity:0;transform:translateY(6px);} to{opacity:1;transform:translateY(0);} }
.tile-dot { width:12px;height:12px;border-radius:50%;background:rgba(212,175,55,0.15);display:flex;align-items:center;justify-content:center; }
.tile-dot-inner { width:5px;height:5px;border-radius:50%;background:#D4AF37; }
.tile-text { font-size:7px;color:rgba(255,255,255,0.4);line-height:1.3;font-weight:500; }
.profile-save-btn { width:100%;padding:8px;background:linear-gradient(135deg,#D4AF37,#F0D060);color:#0A0A0A;border:none;border-radius:8px;font-size:8px;font-weight:700;letter-spacing:0.8px;text-transform:uppercase;cursor:default;display:flex;align-items:center;justify-content:center;gap:4px;animation:tile-slide 0.3s ease both;animation-delay:2.95s; }

.beam-line { position:absolute;top:165px;left:148px;width:90px;height:1px;background:linear-gradient(90deg,#D4AF37,rgba(212,175,55,0));transform-origin:left center;animation:beam-appear 0.4s ease both;animation-delay:1.5s;z-index:5; }
@keyframes beam-appear { from{opacity:0;} to{opacity:1;} }
.beam-particle { position:absolute;top:160px;left:148px;width:6px;height:6px;border-radius:50%;background:#D4AF37;animation:particle-travel 2s ease-in-out infinite;animation-delay:1.6s;z-index:6; }
@keyframes particle-travel { 0%{left:148px;opacity:0;transform:scale(0.5);} 10%{opacity:1;transform:scale(1);} 90%{opacity:1;} 100%{left:228px;opacity:0;transform:scale(0.5);} }
.hero-orb-1 { position:absolute;width:500px;height:500px;border-radius:50%;background:radial-gradient(circle,rgba(212,175,55,0.06) 0%,transparent 65%);top:-100px;right:-100px;pointer-events:none;z-index:0; }

/* ── SECTION SHARED ── */
.lp-section { padding: 5rem 3rem; position: relative; }
.lp-section-inner { max-width: 1180px; margin: 0 auto; }
.lp-eyebrow { display:block;font-size:10px;font-weight:700;letter-spacing:3px;text-transform:uppercase;color:var(--gold);margin-bottom:1.25rem;text-align:center; }
.lp-section-title { font-family:var(--serif);font-size:clamp(2.3rem,4vw,3.5rem);font-weight:600;line-height:1.08;letter-spacing:-0.4px;color:var(--text-primary);text-align:center;margin-bottom:1.25rem; }
.lp-section-title em { font-style:italic;color:var(--gold); }
.lp-section-sub { font-size:15px;font-weight:400;color:var(--text-muted);text-align:center;max-width:520px;margin:0 auto 4rem;line-height:1.9; }

.gold-rule { height:0.5px;background:linear-gradient(90deg,transparent,rgba(212,175,55,0.3),transparent); }
.lp-light .gold-rule { background:linear-gradient(90deg,transparent,rgba(160,120,10,0.2),transparent); }

/* ── TRUST BAR ── */
.lp-trust { padding:2rem 3rem;display:flex;align-items:center;justify-content:center;gap:2.5rem;flex-wrap:wrap;background:var(--surface); }
.lp-trust-label { font-size:10px;font-weight:600;color:var(--text-muted2);letter-spacing:2px;text-transform:uppercase; }
.lp-trust-items { display:flex;align-items:center;gap:2rem;flex-wrap:wrap; }
.lp-trust-item { display:flex;align-items:center;gap:7px;font-size:13px;font-weight:500;color:var(--text-muted); }
.lp-trust-sep { color:var(--border-gold);font-size:18px; }

/* ═══════════════════════════════════════
   ENHANCEMENT 2 — PROGRESS CONNECTOR
   The SVG arrow between steps fills on scroll
═══════════════════════════════════════ */
.lp-how-timeline {
  display: grid;
  grid-template-columns: 1fr 72px 1fr 72px 1fr;
  align-items: start; gap: 0; margin-top: 1rem;
}
.lp-how-bridge {
  display: flex; align-items: flex-start; justify-content: center; padding-top: 2.6rem;
}
/* The SVG path for the arrow */
.how-arrow-path {
  stroke: var(--border-gold);
  stroke-dasharray: 60;
  stroke-dashoffset: 60;
  transition: stroke-dashoffset 0.9s cubic-bezier(0.22,1,0.36,1), stroke 0.4s ease;
}
.how-arrow-path.filled {
  stroke-dashoffset: 0;
  stroke: var(--gold);
}
/* arrowhead */
.how-arrowhead {
  fill: var(--border-gold);
  transition: fill 0.4s ease 0.7s;
}
.how-arrow-path.filled + .how-arrowhead,
.how-arrowhead.filled {
  fill: var(--gold);
}

.lp-how-card {
  background: var(--surface);
  border: 0.5px solid var(--border-color);
  border-radius: 20px; padding: 2.2rem 1.8rem 2rem;
  position: relative; overflow: hidden;
  transition: border-color 0.3s, transform 0.3s;
  cursor: default;
}
.lp-how-card::before { content:'';position:absolute;inset:0;background:radial-gradient(ellipse 80% 60% at 50% -10%,var(--gold-subtle2) 0%,transparent 70%);opacity:0;transition:opacity 0.3s; }
.lp-how-card:hover { border-color: var(--border-gold); transform: translateY(-4px); }
.lp-how-card:hover::before { opacity: 1; }
.lp-how-num { font-family:var(--serif);font-size:7rem;font-weight:700;color:var(--gold-subtle2);line-height:1;position:absolute;bottom:-0.5rem;right:0.8rem;letter-spacing:-3px;pointer-events:none;user-select:none;opacity:0.6; }
.lp-light .lp-how-num { opacity:1;color:rgba(160,120,10,0.1); }
.lp-how-icon-ring { width:56px;height:56px;border-radius:16px;background:var(--gold-subtle);border:1px solid var(--border-gold);display:flex;align-items:center;justify-content:center;font-size:1.6rem;margin-bottom:1.5rem;position:relative; }
.lp-how-icon-ring::after { content:'';position:absolute;inset:-4px;border-radius:20px;border:1px dashed var(--border-gold);animation:lp-spin-slow 12s linear infinite; }
@keyframes lp-spin-slow { to{transform:rotate(360deg);} }
.lp-how-step-label { font-size:9px;font-weight:700;letter-spacing:2.5px;text-transform:uppercase;color:var(--gold);margin-bottom:0.6rem; }
.lp-how-title { font-family:var(--serif);font-size:1.65rem;font-weight:600;color:var(--text-primary);line-height:1.15;margin-bottom:0.75rem; }
.lp-how-desc { font-size:13.5px;color:var(--text-muted);line-height:1.8; }

/* ═══════════════════════════════════════
   ENHANCEMENT 4 — BENTO SHIMMER + SOCIAL PILL STAGGER
═══════════════════════════════════════ */
.lp-feat-bento { display:grid;grid-template-columns:repeat(4,1fr);grid-template-rows:auto auto;gap:10px;margin-top:1rem; }

.lp-feat-bcard {
  background: var(--surface);
  border: 0.5px solid var(--border-color);
  border-radius: 18px; padding: 1.8rem 1.6rem;
  position: relative; overflow: hidden;
  transition: border-color 0.3s, transform 0.25s, background 0.3s;
  cursor: default;
}
/* shimmer sweep on hover */
.lp-feat-bcard::before {
  content: '';
  position: absolute;
  top: 0; left: -100%; width: 60%; height: 100%;
  background: linear-gradient(105deg, transparent, rgba(212,175,55,0.06), transparent);
  transition: left 0s;
  pointer-events: none; z-index: 1;
}
.lp-feat-bcard:hover::before {
  left: 160%;
  transition: left 0.6s ease;
}
.lp-feat-bcard:hover { border-color:var(--border-gold); background:var(--surface2); transform:translateY(-3px); }
.lp-feat-bcard.hero { grid-column:span 2; background:var(--surface2); border-color:var(--border-gold); padding:2.4rem 2.2rem; }
.lp-feat-bcard.tall { grid-row:span 2; display:flex; flex-direction:column; }
.lp-feat-bcard::after { content:'';position:absolute;top:0;left:0;right:0;height:1px;background:transparent;transition:background 0.3s; }
.lp-feat-bcard:hover::after,.lp-feat-bcard.hero::after { background:linear-gradient(90deg,transparent,var(--border-gold),transparent); }

.lp-feat-bicon { width:44px;height:44px;border-radius:12px;background:var(--gold-subtle);border:0.5px solid var(--border-gold);display:flex;align-items:center;justify-content:center;font-size:1.3rem;margin-bottom:1.25rem;flex-shrink:0; }
.lp-feat-bcard.hero .lp-feat-bicon { width:52px;height:52px;font-size:1.6rem;border-radius:14px; }
.lp-feat-btag { display:inline-block;font-size:8.5px;font-weight:700;letter-spacing:2px;text-transform:uppercase;color:var(--gold);margin-bottom:0.5rem;opacity:0.8; }
.lp-feat-btitle { font-family:var(--serif);font-size:1.3rem;font-weight:600;color:var(--text-primary);line-height:1.15;margin-bottom:0.5rem; }
.lp-feat-bcard.hero .lp-feat-btitle { font-size:1.65rem; }
.lp-feat-bdesc { font-size:13px;color:var(--text-muted);line-height:1.8;flex:1; }

.lp-feat-wallet { display:flex;gap:6px;margin-top:1.25rem; }
.lp-wallet-chip { height:26px;border-radius:6px;flex:1;background:var(--gold-subtle);border:0.5px solid var(--border-gold);display:flex;align-items:center;justify-content:center;font-size:9px;font-weight:600;color:var(--gold);letter-spacing:0.5px;position:relative;overflow:hidden; }
/* wallet chip shimmer */
.lp-wallet-chip::after { content:'';position:absolute;top:0;left:-100%;width:50%;height:100%;background:linear-gradient(90deg,transparent,rgba(212,175,55,0.2),transparent);animation:chip-shine 3s ease-in-out infinite; }
.lp-wallet-chip:nth-child(2)::after { animation-delay:0.4s; }
.lp-wallet-chip:nth-child(3)::after { animation-delay:0.8s; }
@keyframes chip-shine { 0%,70%{left:-100%;} 100%{left:200%;} }

/* social pills stagger in when card enters viewport */
.lp-feat-socials { display:flex;flex-wrap:wrap;gap:6px;margin-top:1.1rem; }
.lp-social-pill {
  padding: 4px 10px;
  background: var(--gold-subtle); border: 0.5px solid var(--border-gold);
  border-radius: 100px; font-size: 10px; font-weight: 500; color: var(--text-muted);
  opacity: 0; transform: translateY(6px) scale(0.94);
  transition: opacity 0.35s ease, transform 0.35s ease, background 0.2s, color 0.2s;
}
.lp-social-pill:hover { background: var(--gold-subtle2); color: var(--gold); }
/* stagger delays via nth-child */
.lp-social-pill:nth-child(1) { transition-delay: 0.05s; }
.lp-social-pill:nth-child(2) { transition-delay: 0.12s; }
.lp-social-pill:nth-child(3) { transition-delay: 0.19s; }
.lp-social-pill:nth-child(4) { transition-delay: 0.26s; }
.lp-social-pill:nth-child(5) { transition-delay: 0.33s; }

/* pills animate in when parent card is visible */
.lp-feat-bcard.pills-visible .lp-social-pill {
  opacity: 1; transform: translateY(0) scale(1);
}

.lp-qr-art { margin-top:auto;padding-top:1.5rem;display:grid;grid-template-columns:repeat(5,1fr);grid-template-rows:repeat(5,1fr);gap:3px;opacity:0.2;transition:opacity 0.3s; }
.lp-feat-bcard.tall:hover .lp-qr-art { opacity:0.45; }
.lp-qr-cell { border-radius:2px; }

/* ── USE CASES ── */
.lp-use-tabs { display:flex;flex-wrap:wrap;gap:0.5rem;justify-content:center;margin-bottom:3rem; }
.lp-use-tab { padding:8px 18px;border-radius:100px;font-size:13px;font-weight:600;cursor:pointer;transition:all 0.2s;font-family:var(--sans);display:flex;align-items:center;gap:6px; }
.lp-use-panel { display:grid;grid-template-columns:1fr 1fr;gap:3rem;align-items:center;background:var(--surface);border:0.5px solid var(--border-color);border-radius:24px;padding:3rem;position:relative;overflow:hidden; }
.lp-use-panel::before { content:'';position:absolute;top:0;left:0;right:0;height:1px;background:linear-gradient(90deg,transparent,var(--border-gold),transparent); }
.lp-use-badge { display:inline-flex;align-items:center;gap:8px;padding:6px 14px;border-radius:100px;margin-bottom:1.5rem;font-size:12px;font-weight:700;background:var(--gold-subtle);color:var(--gold);border:0.5px solid var(--border-gold); }
.lp-use-tagline { font-family:var(--serif);font-size:2.2rem;font-weight:600;color:var(--text-primary);line-height:1.1;margin-bottom:1rem; }
.lp-use-desc { font-size:14px;font-weight:400;color:var(--text-muted);line-height:1.9;margin-bottom:2rem; }

/* use case panel transition on tab switch */
.lp-use-content { animation: tab-fade 0.35s cubic-bezier(0.22,1,0.36,1) both; }
@keyframes tab-fade { from{opacity:0;transform:translateY(10px);} to{opacity:1;transform:translateY(0);} }

.lp-use-highlights { display:flex;flex-direction:column;gap:0.75rem; }
.lp-use-hl { display:flex;align-items:center;gap:12px;background:var(--surface2);border:0.5px solid var(--border-color);border-radius:12px;padding:1rem 1.25rem;animation:hl-slide 0.3s ease both; }
.lp-use-hl:nth-child(1){animation-delay:0.05s;} .lp-use-hl:nth-child(2){animation-delay:0.12s;} .lp-use-hl:nth-child(3){animation-delay:0.19s;} .lp-use-hl:nth-child(4){animation-delay:0.26s;}
@keyframes hl-slide { from{opacity:0;transform:translateX(12px);} to{opacity:1;transform:translateX(0);} }
.lp-use-hl-check { width:28px;height:28px;border-radius:50%;display:flex;align-items:center;justify-content:center;flex-shrink:0;font-size:11px;font-weight:700;background:var(--gold-subtle);color:var(--gold); }
.lp-use-hl-text { font-size:13px;font-weight:500;color:var(--text-muted); }

/* ── PRICING + CTA MERGED ── */
.lp-pricing-cta { padding:5rem 3rem;position:relative;overflow:hidden; }
.lp-pricing-cta::before { content:'';position:absolute;inset:0;background:radial-gradient(ellipse 70% 80% at 50% 50%,var(--gold-subtle) 0%,transparent 70%);pointer-events:none; }
.lp-pricing-cta-inner { max-width:1180px;margin:0 auto;display:grid;grid-template-columns:1fr 1.1fr;gap:4rem;align-items:center;position:relative; }
.lp-pricing-eyebrow { display:block;font-size:10px;font-weight:700;letter-spacing:3px;text-transform:uppercase;color:var(--gold);margin-bottom:1.25rem; }
.lp-pricing-headline { font-family:var(--serif);font-size:clamp(2.8rem,5vw,4.2rem);font-weight:600;line-height:1.05;color:var(--text-primary);margin-bottom:1.25rem;letter-spacing:-0.5px; }
.lp-pricing-headline em { font-style:italic;color:var(--gold); }
.lp-pricing-sub { font-size:15px;color:var(--text-muted);line-height:1.9;margin-bottom:2.5rem;max-width:400px; }
.lp-pricing-perks { display:flex;flex-direction:column;gap:0.6rem;margin-bottom:2.5rem; }
.lp-pricing-perk { display:flex;align-items:center;gap:10px;font-size:14px;font-weight:500;color:var(--text-muted); }
.lp-perk-tick { width:20px;height:20px;border-radius:50%;flex-shrink:0;background:var(--gold-subtle);border:0.5px solid var(--border-gold);display:flex;align-items:center;justify-content:center;font-size:9px;color:var(--gold);font-weight:700; }

.lp-price-card { background:var(--surface);border:0.5px solid var(--border-gold);border-radius:24px;padding:2.5rem;position:relative;overflow:hidden; }
.lp-price-card::before { content:'';position:absolute;top:0;left:10%;right:10%;height:1px;background:linear-gradient(90deg,transparent,var(--gold),transparent); }
.lp-price-card-top { display:flex;align-items:flex-start;justify-content:space-between;margin-bottom:1.5rem; }
.lp-price-badge { display:inline-block;background:linear-gradient(135deg,#D4AF37,#F0D060);color:#0A0A0A;border-radius:100px;padding:4px 14px;font-size:10px;font-weight:700;letter-spacing:1px;text-transform:uppercase; }
.lp-price-amount { text-align:right; }
.lp-price-num { font-family:var(--serif);font-size:4rem;font-weight:600;color:var(--text-primary);line-height:1;letter-spacing:-2px; }
.lp-price-period { font-size:10px;font-weight:600;color:var(--text-muted2);letter-spacing:2px;text-transform:uppercase;margin-top:4px; }
.lp-price-divider { height:0.5px;background:var(--border-color);margin:1.25rem 0; }
.lp-price-list { list-style:none;display:flex;flex-direction:column;gap:0;margin-bottom:1.75rem; }
.lp-price-item { display:flex;align-items:center;gap:10px;padding:0.55rem 0;font-size:13px;font-weight:500;color:var(--text-muted);border-bottom:0.5px solid var(--border-color); }
.lp-price-item:last-child { border-bottom:none; }
.lp-price-check { color:var(--gold);font-size:11px;flex-shrink:0; }

/* ── FOOTER ── */
.lp-footer { padding:3rem 3rem 4rem;border-top:0.5px solid var(--border-color);background:var(--surface); }
.lp-footer-inner { max-width:1180px;margin:0 auto;display:flex;flex-direction:column;align-items:center;gap:1rem; }
.lp-footer-logo { display:flex;align-items:center;gap:10px;font-family:var(--sans);font-size:15px;font-weight:600;color:var(--text-primary); }
.lp-footer-logo em { color:var(--gold);font-style:normal;font-weight:700; }
.lp-footer-tagline { font-size:13px;font-weight:400;color:var(--text-muted2); }
.lp-footer-copy { font-size:12px;font-weight:400;color:var(--text-muted2);opacity:0.6; }

/* ── THEME TOGGLE ── */
.lp-theme-btn { width:40px;height:40px;border-radius:50%;border:0.5px solid var(--border-gold);background:var(--gold-subtle);cursor:pointer;display:flex;align-items:center;justify-content:center;transition:background 0.2s,transform 0.15s;flex-shrink:0; }
.lp-theme-btn:hover { background:var(--gold-subtle2);transform:scale(1.08); }

/* ── MOBILE ── */
@media (max-width: 900px) {
  .lp-hero { grid-template-columns:1fr;text-align:center;padding:8rem 1.5rem 4rem; }
  .lp-hero-sub { margin:0 auto 1rem; }
  .lp-hero-ctas { justify-content:center; }
  .lp-hero-stats { justify-content:center; }
  .lp-hero-right { margin-top:3rem; }
  .scan-scene { width:300px;height:380px;transform:scale(0.85); }
  .lp-how-timeline { grid-template-columns:1fr; }
  .lp-how-bridge { display:none; }
  .lp-feat-bento { grid-template-columns:1fr 1fr; }
  .lp-feat-bcard.hero { grid-column:span 2; }
  .lp-feat-bcard.tall { grid-row:span 1; }
  .lp-use-panel { grid-template-columns:1fr; }
  .lp-pricing-cta-inner { grid-template-columns:1fr; }
  .lp-nav { padding:1rem 1.5rem; }
  .lp-nav.scrolled { padding:0.75rem 1.5rem; }
  .lp-nav-links { gap:1rem; }
  .lp-section { padding:4rem 1.5rem; }
  .lp-pricing-cta { padding:4rem 1.5rem; }
}
@media (max-width: 600px) {
  .lp-feat-bento { grid-template-columns:1fr; }
  .lp-feat-bcard.hero { grid-column:span 1; }
  .lp-nav-link { display:none; }
}
`;

/* ─────────────────────────────────────────
   ENHANCEMENT 1 — COUNT-UP HOOK
───────────────────────────────────────── */
function useCountUp(target, duration = 1800, suffix = "", started = false) {
    const [display, setDisplay] = useState("0");
    const [progress, setProgress] = useState(0);
    const frameRef = useRef(null);

    useEffect(() => {
        if (!started) return;
        // parse numeric target
        const num = parseFloat(target.replace(/[^0-9.]/g, ""));
        if (!num) { setDisplay(target); setProgress(100); return; }

        const startTime = performance.now();
        const animate = (now) => {
            const elapsed = now - startTime;
            const pct = Math.min(elapsed / duration, 1);
            // easeOutExpo
            const eased = pct === 1 ? 1 : 1 - Math.pow(2, -10 * pct);
            const current = num * eased;
            const prog = Math.round(pct * 100);

            // format
            let formatted;
            if (Number.isInteger(num)) {
                formatted = Math.round(current).toString();
            } else {
                formatted = current.toFixed(0);
            }
            setDisplay(formatted + suffix);
            setProgress(prog);

            if (pct < 1) {
                frameRef.current = requestAnimationFrame(animate);
            }
        };
        frameRef.current = requestAnimationFrame(animate);
        return () => cancelAnimationFrame(frameRef.current);
    }, [started, target, duration, suffix]);

    return { display, progress };
}

/* ─────────────────────────────────────────
   STAT ITEM
───────────────────────────────────────── */
function StatItem({ value, suffix, label, started, delay = 0 }) {
    const [go, setGo] = useState(false);
    useEffect(() => {
        if (!started) return;
        const t = setTimeout(() => setGo(true), delay);
        return () => clearTimeout(t);
    }, [started, delay]);

    const { display, progress } = useCountUp(value, 1600, suffix, go);
    const isCounting = go && progress < 100;

    return (
        <div className="lp-stat">
            <div className={`lp-stat-num${isCounting ? " counting" : ""}`}>
                {value === "0" ? "0" : display}
            </div>
            <div className="lp-stat-label">{label}</div>
            <div className="lp-stat-bar">
                <div className="lp-stat-bar-fill" style={{ width: `${progress}%` }} />
            </div>
        </div>
    );
}

/* ─────────────────────────────────────────
   QR / SCAN SCENE
───────────────────────────────────────── */
const QR_PATTERN = [
    [1, 1, 1, 0, 1, 0], [1, 0, 1, 1, 0, 1], [1, 1, 1, 0, 1, 1], [0, 1, 0, 1, 0, 0], [1, 0, 1, 1, 1, 0], [0, 1, 0, 0, 1, 1],
];

function ScanScene() {
    return (
        <div className="scan-scene">
            <div className="hero-orb-1" />

            {/* Phone A — scanner */}
            <div className="phone-scanner">
                <div className="phone-scanner-screen">
                    <div className="scanner-viewfinder">
                        <div className="vf-corner-tr" />
                        <div className="vf-corner-bl" />
                        <div className="scan-laser" />
                        <div className="mini-qr">
                            {QR_PATTERN.map((row, ri) =>
                                row.map((cell, ci) => (
                                    <div key={`${ri}-${ci}`} className="mini-qr-cell"
                                        style={{ background: cell ? "rgba(212,175,55,0.5)" : "transparent" }} />
                                ))
                            )}
                        </div>
                        {/* Enhancement 5: flash overlay */}
                        <div className="scanner-success-flash" />
                    </div>
                    <div className="scanner-label">Point at QR code</div>
                </div>
            </div>

            <div className="beam-line" />
            <div className="beam-particle" />

            {/* Enhancement 5: ripple rings */}
            <div className="scan-ripple r1" />
            <div className="scan-ripple r2" />
            <div className="scan-ripple r3" />
            {/* Enhancement 5: success badge */}
            <div className="scan-success">
                <div className="scan-success-dot" />
                Contact Saved ✓
            </div>

            {/* Phone B */}
            <div className="phone-profile">
                <div className="profile-card-wrap">
                    <div className="profile-banner">
                        <div className="profile-banner-img" />
                        <div className="profile-banner-fade" />
                        <div className="profile-avatar-ring">
                            <div className="profile-avatar-inner">UN</div>
                        </div>
                    </div>
                    <div className="profile-info">
                        <div className="profile-info-name">User <em>Name</em></div>
                        <div className="profile-info-role">Director · Company Name</div>
                        <div className="profile-tiles">
                            {[["📞", "Phone"], ["✉️", "Email"], ["📍", "Delhi"]].map(([, label]) => (
                                <div className="profile-tile" key={label}>
                                    <div className="tile-dot"><div className="tile-dot-inner" /></div>
                                    <div className="tile-text">{label}</div>
                                </div>
                            ))}
                        </div>
                        <button className="profile-save-btn">↓ Save Contact</button>
                        <div className="profile-tiles" style={{ marginTop: "1rem" }}>
                            {[["🌐", "Website"], ["📄", "Resume"], ["ⓕ", "Facebook"]].map(([, label]) => (
                                <div className="profile-tile" key={label}>
                                    <div className="tile-dot"><div className="tile-dot-inner" /></div>
                                    <div className="tile-text">{label}</div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

/* ─────────────────────────────────────────
   PROGRESS ARROW BRIDGE (Enhancement 2)
───────────────────────────────────────── */
function ArrowBridge({ filled }) {
    return (
        <div className="lp-how-bridge">
            <svg width="36" height="28" viewBox="0 0 36 28" fill="none" overflow="visible">
                {/* shaft */}
                <path
                    d="M2 14 L26 14"
                    className={`how-arrow-path${filled ? " filled" : ""}`}
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    fill="none"
                    style={{ strokeDasharray: 28, strokeDashoffset: filled ? 0 : 28, transition: "stroke-dashoffset 0.8s cubic-bezier(0.22,1,0.36,1), stroke 0.4s ease" }}
                />
                {/* head */}
                <path
                    d="M20 7 L28 14 L20 21"
                    className={`how-arrow-path${filled ? " filled" : ""}`}
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    fill="none"
                    style={{ strokeDasharray: 24, strokeDashoffset: filled ? 0 : 24, transition: "stroke-dashoffset 0.6s cubic-bezier(0.22,1,0.36,1) 0.3s, stroke 0.4s ease 0.3s" }}
                />
            </svg>
        </div>
    );
}

/* ─────────────────────────────────────────
   USE CASES
───────────────────────────────────────── */
const USE_CASES = [
    { icon: "👔", role: "Job Seekers", tagline: "Land your next opportunity faster", desc: "Walk into every interview with a digital card that includes your resume, LinkedIn, GitHub, and portfolio — all in one elegant scan.", highlights: ["Resume attached & downloadable", "LinkedIn & GitHub links", "QR code for instant sharing", "Save contact in one tap"] },
    { icon: "💼", role: "Freelancers", tagline: "Win clients before you even speak", desc: "Share everything a client needs — portfolio, social proof, and contact — all in a single professional link. Close deals faster.", highlights: ["Portfolio & website link", "WhatsApp quick contact", "Instagram & social proof", "Professional digital presence"] },
    { icon: "🏢", role: "Business Owners", tagline: "Network smarter, not harder", desc: "Stop spending on printed cards that get lost. Share via QR at events — always updated, never outdated, never thrown away.", highlights: ["Always up-to-date details", "Google Wallet for offline use", "QR code for events & meetups", "Share via WhatsApp or email"] },
    { icon: "🎓", role: "Students", tagline: "Build your professional identity early", desc: "Stand out in campus placements and internship drives. Show your skills, projects, and social profiles before you say a word.", highlights: ["GitHub & project links", "Resume upload for placements", "LinkedIn profile showcase", "Free forever — no cost"] },
    { icon: "📣", role: "Sales Pros", tagline: "Close the follow-up gap instantly", desc: "At trade shows or cold visits — one QR scan and your prospect has everything. Number, email, website, product links in one place.", highlights: ["Instant QR share at events", "Website & product links", "Save contact in one tap", "Google Wallet for quick access"] },
    { icon: "🧑‍💻", role: "Developers", tagline: "Your entire dev profile in one link", desc: "GitHub, LinkedIn, Twitter, personal site, resume — all in one clean card. Share at hackathons or in open source communities.", highlights: ["GitHub & portfolio links", "Twitter / X profile", "Resume for job applications", "Professional dev identity"] },
];

function UseCasesSection({ navigate }) {
    const [active, setActive] = useState(0);
    const [tabKey, setTabKey] = useState(0);
    const cur = USE_CASES[active];

    const switchTab = (i) => {
        setActive(i);
        setTabKey(k => k + 1);
    };

    return (
        <section className="lp-section">
            <div className="lp-section-inner">
                <span className="lp-eyebrow reveal from-up">Who is it for</span>
                <h2 className="lp-section-title reveal from-up d1">Built for every <em>professional</em></h2>
                <p className="lp-section-sub reveal from-up d2">Whether you're a student or a CEO — your digital card works for you.</p>

                <div className="lp-use-tabs reveal from-up d3">
                    {USE_CASES.map((u, i) => (
                        <button key={i} className="lp-use-tab" onClick={() => switchTab(i)}
                            style={{
                                border: active === i ? "0.5px solid var(--border-gold)" : "0.5px solid var(--border-color)",
                                background: active === i ? "var(--gold-subtle2)" : "transparent",
                                color: active === i ? "var(--gold)" : "var(--text-muted)",
                            }}>
                            {u.icon} {u.role}
                        </button>
                    ))}
                </div>

                <div className="lp-use-panel reveal from-up d4" style={{ borderColor: "var(--border-gold)" }}>
                    {/* Left content — re-animates on tab switch */}
                    <div key={`left-${tabKey}`} className="lp-use-content">
                        <div className="lp-use-badge"><span>{cur.icon}</span><span style={{ fontSize: "12px", letterSpacing: "0.5px" }}>{cur.role}</span></div>
                        <div className="lp-use-tagline">{cur.tagline}</div>
                        <p className="lp-use-desc">{cur.desc}</p>
                        <button className="lp-btn-gold" onClick={() => navigate("/create")}>Create My Card — Free →</button>
                    </div>

                    {/* Right highlights — each item slides in from right */}
                    <div key={`right-${tabKey}`} className="lp-use-highlights">
                        {cur.highlights.map((h, i) => (
                            <div className="lp-use-hl" key={i} style={{ animationDelay: `${0.05 + i * 0.07}s` }}>
                                <div className="lp-use-hl-check">✓</div>
                                <span className="lp-use-hl-text">{h}</span>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </section>
    );
}

/* ─────────────────────────────────────────
   MAIN COMPONENT
───────────────────────────────────────── */
export default function Viewthree() {
    const navigate = useNavigate();
    const [scrolled, setScrolled] = useState(false);
    const [isLight, setIsLight] = useState(false);
    const [statsStarted, setStatsStarted] = useState(false);
    // Enhancement 2: track if how-section is in view for arrow progress
    const [howVisible, setHowVisible] = useState(false);
    // Enhancement 4: track when social card enters view
    const socialCardRef = useRef(null);

    const statsRef = useRef(null);
    const howRef = useRef(null);

    /* ── scroll nav ── */
    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 40);
        window.addEventListener("scroll", onScroll);
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    /* ── unified IntersectionObserver ── */
    useEffect(() => {
        const observers = [];

        // 1. directional reveals
        const revealEls = document.querySelectorAll(".reveal");
        const revealObs = new IntersectionObserver(
            (entries) => entries.forEach(e => { if (e.isIntersecting) e.target.classList.add("visible"); }),
            { threshold: 0.1 }
        );
        revealEls.forEach(el => revealObs.observe(el));
        observers.push(revealObs);

        // 2. count-up trigger
        if (statsRef.current) {
            const statsObs = new IntersectionObserver(
                ([entry]) => { if (entry.isIntersecting) setStatsStarted(true); },
                { threshold: 0.5 }
            );
            statsObs.observe(statsRef.current);
            observers.push(statsObs);
        }

        // 3. how-section for arrow progress connector
        if (howRef.current) {
            const howObs = new IntersectionObserver(
                ([entry]) => { if (entry.isIntersecting) setHowVisible(true); },
                { threshold: 0.3 }
            );
            howObs.observe(howRef.current);
            observers.push(howObs);
        }

        // 4. social pills card
        const pillCards = document.querySelectorAll(".lp-feat-bcard.has-pills");
        const pillObs = new IntersectionObserver(
            (entries) => entries.forEach(e => { if (e.isIntersecting) e.target.classList.add("pills-visible"); }),
            { threshold: 0.4 }
        );
        pillCards.forEach(c => pillObs.observe(c));
        observers.push(pillObs);

        return () => observers.forEach(o => o.disconnect());
    }, []);

    return (
        <div className={isLight ? "lp-light" : ""} style={{ background: isLight ? "#FFFFFF" : "#080808", minHeight: "100vh" }}>
            <style dangerouslySetInnerHTML={{ __html: GLOBAL_CSS }} />

            {/* ── NAV ── */}
            <nav className={`lp-nav${scrolled ? " scrolled" : ""}`}>
                <div className="lp-nav-inner">
                    <a href="/" className="lp-logo">
                        <svg className="lp-logo-icon" viewBox="0 0 34 34" fill="none">
                            <rect width="34" height="34" rx="9" fill={isLight ? "#F0EDE6" : "#1A1A1A"} />
                            <rect width="34" height="34" rx="9" stroke="var(--border-gold)" strokeWidth="0.5" />
                            <circle cx="15" cy="13" r="5" stroke="var(--gold)" strokeWidth="1.5" fill="none" />
                            <path d="M7 27 C7 21 10 18 15 18 C20 18 23 21 23 27" stroke="var(--gold)" strokeWidth="1.5" strokeLinecap="round" fill="none" />
                            <path d="M23 11 L27 11 L27 23" stroke="var(--gold)" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" fill="none" opacity="0.6" />
                            <circle cx="27" cy="23" r="1.8" fill="var(--gold)" />
                        </svg>
                        myprofile<em>corner</em>
                    </a>
                    <div className="lp-nav-links">
                        <a href="#how" className="lp-nav-link">How it works</a>
                        <a href="#features" className="lp-nav-link">Features</a>
                        <a href="#pricing" className="lp-nav-link">Pricing</a>
                        <button className="lp-theme-btn" onClick={() => setIsLight(p => !p)} aria-label="Toggle theme">
                            {isLight ? (
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--gold)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
                                </svg>
                            ) : (
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--gold)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <circle cx="12" cy="12" r="5" /><line x1="12" y1="1" x2="12" y2="3" /><line x1="12" y1="21" x2="12" y2="23" />
                                    <line x1="4.22" y1="4.22" x2="5.64" y2="5.64" /><line x1="18.36" y1="18.36" x2="19.78" y2="19.78" />
                                    <line x1="1" y1="12" x2="3" y2="12" /><line x1="21" y1="12" x2="23" y2="12" />
                                    <line x1="4.22" y1="19.78" x2="5.64" y2="18.36" /><line x1="18.36" y1="5.64" x2="19.78" y2="4.22" />
                                </svg>
                            )}
                        </button>
                        <button className="lp-nav-cta" onClick={() => navigate("/create")}>Create Free Card</button>
                    </div>
                </div>
            </nav>

            {/* ── HERO ── */}
            <section style={{ background: isLight ? "#FFFFFF" : "#080808", overflow: "hidden", position: "relative" }}>
                <div className="lp-hero">
                    <div style={{ position: "absolute", width: "600px", height: "600px", borderRadius: "50%", background: "radial-gradient(circle, var(--gold-subtle) 0%, transparent 65%)", top: "-200px", left: "-100px", pointerEvents: "none" }} />

                    <div className="lp-hero-left">
                        <div className="lp-hero-eyebrow reveal from-down">
                            <div className="lp-hero-eyebrow-dot" /> 100% Free · No App Needed
                        </div>
                        <h1 className="lp-hero-title reveal from-left">
                            Your Digital<br />Identity, <em>Always</em><br />in Your Pocket
                        </h1>
                        <p className="lp-hero-sub reveal from-left d1">
                            Replace paper visiting cards with a smart digital card. Share your profile instantly via QR code, link, or Google Wallet — no printing, no waste.
                        </p>

                        {/* Enhancement 1: Count-up stats */}
                        <div className="lp-hero-stats" ref={statsRef}>
                            <StatItem value="100" suffix="%" label="Free forever" started={statsStarted} delay={0} />
                            <StatItem value="2" suffix=" min" label="Setup time" started={statsStarted} delay={200} />
                            <StatItem value="0" suffix="" label="Paper waste" started={statsStarted} delay={400} />
                        </div>

                        <div className="lp-hero-ctas reveal from-up d2">
                            <button className="lp-btn-gold" onClick={() => navigate("/create")}>Create My Card — It's Free →</button>
                            <a href="#how" className="lp-btn-ghost">See how it works</a>
                        </div>
                    </div>

                    <div className="lp-hero-right reveal from-right">
                        <ScanScene />
                    </div>
                </div>
            </section>

            <div className="gold-rule" />

            {/* ── TRUST BAR ── */}
            <div className="lp-trust">
                <span className="lp-trust-label">Works seamlessly with</span>
                <div className="lp-trust-items">
                    {[["🔥", "Firebase"], ["🪙", "Google Wallet"], ["📱", "Android & iOS"], ["🔗", "Any Browser"]].map(([icon, label], i, arr) => (
                        <React.Fragment key={label}>
                            <div className="lp-trust-item"><span>{icon}</span><span>{label}</span></div>
                            {i < arr.length - 1 && <span className="lp-trust-sep">·</span>}
                        </React.Fragment>
                    ))}
                </div>
            </div>

            <div className="gold-rule" />

            {/* ── HOW IT WORKS ── */}
            <section id="how" className="lp-section" ref={howRef}>
                <div className="lp-section-inner">
                    <span className="lp-eyebrow reveal from-up">How it works</span>
                    <h2 className="lp-section-title reveal from-up d1">From setup to sharing <em>in 3 steps</em></h2>
                    <p className="lp-section-sub reveal from-up d2">No app downloads. No tech skills required. Just fill, share, impress.</p>

                    {/* Enhancement 2: progress connectors + directional card reveals */}
                    <div className="lp-how-timeline">
                        <div className="lp-how-card reveal from-left d1">
                            <div className="lp-how-num">01</div>
                            <div className="lp-how-icon-ring">✏️</div>
                            <div className="lp-how-step-label">Step 01</div>
                            <div className="lp-how-title">Fill Your Profile</div>
                            <p className="lp-how-desc">Add your name, photo, contact info, social links, resume & website. Takes less than 2 minutes.</p>
                        </div>

                        <ArrowBridge filled={howVisible} />

                        <div className="lp-how-card reveal from-up d3">
                            <div className="lp-how-num">02</div>
                            <div className="lp-how-icon-ring">🎴</div>
                            <div className="lp-how-step-label">Step 02</div>
                            <div className="lp-how-title">Get Your Digital Card</div>
                            <p className="lp-how-desc">Instantly get a beautiful card with a unique QR code & shareable link — no design skills needed.</p>
                        </div>

                        <ArrowBridge filled={howVisible} />

                        <div className="lp-how-card reveal from-right d5">
                            <div className="lp-how-num">03</div>
                            <div className="lp-how-icon-ring">🚀</div>
                            <div className="lp-how-step-label">Step 03</div>
                            <div className="lp-how-title">Share Anywhere</div>
                            <p className="lp-how-desc">Show QR in meetings, add to Google Wallet, or send your link via WhatsApp, email, or LinkedIn.</p>
                        </div>
                    </div>
                </div>
            </section>

            <div className="gold-rule" />

            {/* ── FEATURES ── */}
            <section id="features" className="lp-section">
                <div className="lp-section-inner">
                    <span className="lp-eyebrow reveal from-up">Features</span>
                    <h2 className="lp-section-title reveal from-up d1">Everything a modern <em>professional</em> needs</h2>
                    <p className="lp-section-sub reveal from-up d2">One card. Every detail. Always up-to-date.</p>

                    {/* Enhancement 4: bento with shimmer + social pill stagger */}
                    <div className="lp-feat-bento">

                        {/* Hero: Google Wallet — reveals from left */}
                        <div className="lp-feat-bcard hero reveal from-left d1">
                            <div className="lp-feat-bicon">🪙</div>
                            <div className="lp-feat-btag">Premium Feature</div>
                            <div className="lp-feat-btitle">Google Wallet Pass</div>
                            <p className="lp-feat-bdesc">Add your card to Google Wallet and access it offline — at meetings, events, or anywhere without internet.</p>
                            <div className="lp-feat-wallet">
                                <div className="lp-wallet-chip">G Pay</div>
                                <div className="lp-wallet-chip">Wallet</div>
                                <div className="lp-wallet-chip">Offline ✓</div>
                            </div>
                        </div>

                        {/* QR tall — reveals from right */}
                        <div className="lp-feat-bcard tall reveal from-right d2">
                            <div className="lp-feat-bicon">📲</div>
                            <div className="lp-feat-btag">Instant Share</div>
                            <div className="lp-feat-btitle">Unique QR Code</div>
                            <p className="lp-feat-bdesc">Every card gets a dedicated QR. One scan opens your full profile — no typing, no friction.</p>
                            <div className="lp-qr-art">
                                {[1, 1, 1, 0, 1, 1, 0, 1, 0, 0, 1, 1, 1, 0, 1, 0, 1, 0, 1, 0, 1, 0, 1, 1, 1].map((v, i) => (
                                    <div key={i} className="lp-qr-cell" style={{ background: v ? "var(--gold)" : "var(--gold-subtle)", borderRadius: "2px", aspectRatio: "1" }} />
                                ))}
                            </div>
                        </div>

                        {/* Photo — from below */}
                        <div className="lp-feat-bcard reveal from-up d3">
                            <div className="lp-feat-bicon">📸</div>
                            <div className="lp-feat-btag">Identity</div>
                            <div className="lp-feat-btitle">Profile Photo</div>
                            <p className="lp-feat-bdesc">Your face on your card — and inside Google Wallet too.</p>
                        </div>

                        {/* Save contact — from below */}
                        <div className="lp-feat-bcard reveal from-up d4">
                            <div className="lp-feat-bicon">💾</div>
                            <div className="lp-feat-btag">One Tap</div>
                            <div className="lp-feat-btitle">Save Contact</div>
                            <p className="lp-feat-bdesc">Visitors save you directly to their phone contacts with a single tap.</p>
                        </div>

                        {/* Hero: Social Links — from left, pills stagger */}
                        <div className="lp-feat-bcard hero has-pills reveal from-left d2">
                            <div className="lp-feat-bicon">🔗</div>
                            <div className="lp-feat-btag">Connections</div>
                            <div className="lp-feat-btitle">All Social Links</div>
                            <p className="lp-feat-bdesc">LinkedIn, GitHub, Instagram, Twitter, WhatsApp, YouTube — everything that matters in one place.</p>
                            <div className="lp-feat-socials">
                                {["in LinkedIn", "⌥ GitHub", "✉ WhatsApp", "▶ YouTube", "📷 Insta"].map(s => (
                                    <span key={s} className="lp-social-pill">{s}</span>
                                ))}
                            </div>
                        </div>

                        <div className="lp-feat-bcard reveal from-up d3">
                            <div className="lp-feat-bicon">📄</div>
                            <div className="lp-feat-btag">Career</div>
                            <div className="lp-feat-btitle">Resume Upload</div>
                            <p className="lp-feat-bdesc">Attach your PDF resume. Recruiters can download it instantly.</p>
                        </div>
                        <div className="lp-feat-bcard reveal from-up d4">
                            <div className="lp-feat-bicon">✏️</div>
                            <div className="lp-feat-btag">Always Fresh</div>
                            <div className="lp-feat-btitle">Edit Anytime</div>
                            <p className="lp-feat-bdesc">Changed jobs? New number? Update instantly — same link forever.</p>
                        </div>
                        <div className="lp-feat-bcard reveal from-right d5">
                            <div className="lp-feat-bicon">🌐</div>
                            <div className="lp-feat-btag">Universal</div>
                            <div className="lp-feat-btitle">Works Everywhere</div>
                            <p className="lp-feat-bdesc">Opens in any browser on any device. No app install ever required.</p>
                        </div>

                    </div>
                </div>
            </section>

            <div className="gold-rule" />

            {/* ── USE CASES ── */}
            <UseCasesSection navigate={navigate} />

            <div className="gold-rule" />

            {/* ── PRICING + CTA MERGED ── */}
            <section id="pricing" className="lp-pricing-cta">
                <div className="lp-pricing-cta-inner">
                    <div className="lp-pricing-copy">
                        <span className="lp-pricing-eyebrow reveal from-left">Pricing</span>
                        <h2 className="lp-pricing-headline reveal from-left d1">Stop carrying<br /><em>paper cards.</em></h2>
                        <p className="lp-pricing-sub reveal from-left d2">Create your digital card in under 2 minutes. No hidden charges. No credit card. Free forever.</p>
                        <div className="lp-pricing-perks reveal from-left d3">
                            {["No app download needed", "Unique QR code & shareable link", "Google Wallet pass included", "Edit anytime — same link forever", "Works on every device & browser"].map(p => (
                                <div className="lp-pricing-perk" key={p}>
                                    <div className="lp-perk-tick">✓</div><span>{p}</span>
                                </div>
                            ))}
                        </div>
                        <button className="lp-btn-gold reveal from-up d4" onClick={() => navigate("/create")}>Create My Card Now →</button>
                    </div>
                    <div className="lp-price-card reveal from-right d2">
                        <div className="lp-price-card-top">
                            <div className="lp-price-badge">Most Popular</div>
                            <div className="lp-price-amount">
                                <div className="lp-price-num">₹0</div>
                                <div className="lp-price-period">Forever Free</div>
                            </div>
                        </div>
                        <div className="lp-price-divider" />
                        <ul className="lp-price-list">
                            {["1 Digital Visiting Card", "Unique QR Code & Link", "Profile Photo Upload", "Resume Upload (PDF)", "All Social Media Links", "Google Wallet Pass", "Save Contact (.vcf)", "Edit Anytime", "No App Required"].map(item => (
                                <li className="lp-price-item" key={item}><span className="lp-price-check">✦</span>{item}</li>
                            ))}
                        </ul>
                        <button className="lp-btn-gold" style={{ width: "100%", justifyContent: "center" }} onClick={() => navigate("/create")}>Get Started Free →</button>
                    </div>
                </div>
            </section>

            <div className="gold-rule" />

            {/* ── FOOTER ── */}
            <footer className="lp-footer">
                <div className="lp-footer-inner">
                    <div className="lp-footer-logo">
                        <svg width="28" height="28" viewBox="0 0 34 34" fill="none">
                            <rect width="34" height="34" rx="9" fill={isLight ? "#F0EDE6" : "#1A1A1A"} />
                            <rect width="34" height="34" rx="9" stroke="var(--border-gold)" strokeWidth="0.5" />
                            <circle cx="15" cy="13" r="5" stroke="var(--gold)" strokeWidth="1.5" fill="none" />
                            <path d="M7 27 C7 21 10 18 15 18 C20 18 23 21 23 27" stroke="var(--gold)" strokeWidth="1.5" strokeLinecap="round" fill="none" />
                            <path d="M23 11 L27 11 L27 23" stroke="var(--gold)" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" fill="none" opacity="0.6" />
                            <circle cx="27" cy="23" r="1.8" fill="var(--gold)" />
                        </svg>
                        myprofile<em>corner</em>
                    </div>
                    <p className="lp-footer-tagline">Your digital identity, always in your pocket.</p>
                    <p className="lp-footer-copy">© {new Date().getFullYear()} MyProfileCorner · Made with ♥ in India</p>
                </div>
            </footer>
        </div>
    );
}
